<script setup>
import { computed } from "vue";

import { learnMoreItemClicked } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { captureMessageWithContext } from "@/sentry";
import { useAppStore } from "@/stores";

import SvgIcon from "./SvgIcon.vue";
import { articles } from "./articles";

const typeIcons = {
  article: "general/learn",
};

const props = defineProps({ id: { type: String, required: true } });

const store = useAppStore();

const entry = computed(() => {
  const article = articles[props.id];
  if (!article) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[LearnMoreItem] Unknown article id: "${props.id}"`);
    } else {
      void captureMessageWithContext("LearnMoreItem: unknown article id", {
        learnMore: { articleId: props.id },
      });
    }
    return null;
  }
  return article;
});

const icon = computed(() => typeIcons[entry.value?.type] || "general/learn");

function open() {
  const { articleId, url } = entry.value;
  const intercomReady = store.intercomAvailable && typeof window.Intercom === "function";
  if (articleId && intercomReady) {
    window.Intercom("showArticle", articleId);
    trackEvent(learnMoreItemClicked({ id: props.id, target: "intercom" }));
  } else if (url) {
    window.open(url, "_blank", "noopener");
    trackEvent(learnMoreItemClicked({ id: props.id, target: "external" }));
  }
}
</script>

<template>
  <li
    v-if="entry"
    tabindex="-1"
    role="menuitem"
    class="list-item learn-more-item"
    @click="open"
    @keydown.enter="open"
  >
    <SvgIcon :name="icon" width="18" height="18" class="icon" />
    {{ entry.title }}
  </li>
</template>

<style lang="scss" scoped>
.learn-more-item {
  gap: 8px;

  .icon {
    flex-shrink: 0;
  }
}
</style>
